import type { StaticPillVariant } from '@app/shared/vue/components/sf-static-pill/constants'
import type {
  FilterOption,
  GroupColumn,
  TwoFactorStatus,
  UserAccess,
  UserColumn,
  UserList,
  UserPanelCapabilities,
  UserRole,
  UserStatus,
} from '@app/application/user-management/types'

import { staticPillVariants } from '@app/shared/vue/components/sf-static-pill/constants'
import {
  footerMailActions,
  groupColumns,
  twoFactorStatuses,
  userColumns,
  userLastLoginFilters,
  userListRoles,
  userLists,
  userRoles,
  userStatuses,
} from '@app/application/user-management/types'

export const SKELETON_ROWS = 5

export const PANEL_SKELETON_ROWS = 4

export const PANEL_TOAST_ID = 'user-panel'

export const PANEL_SUCCESS_TOAST_DISMISS_MS = 5000

export const TWO_FACTOR_TOAST_ID = 'user-panel-two-factor'

export const ADD_BUTTON_KEYS: Record<UserList, string> = {
  staff: 'user.add_staff',
  external: 'user.add_external',
}

export const EMPTY_STATE_KEYS: Record<UserList, string> = {
  staff: 'user.empty_states.staff',
  external: 'user.empty_states.external_contributor',
}

export const STAFF_COLUMNS: UserColumn[] = [
  userColumns.NAME,
  userColumns.STATUS,
  userColumns.ROLE,
  userColumns.ACCESS,
  userColumns.FILES,
  userColumns.LAST_LOGIN,
  userColumns.ACTIONS,
]

export const EXTERNAL_COLUMNS: UserColumn[] = STAFF_COLUMNS.filter(
  (column) => column !== userColumns.ACCESS
)

export const COLUMN_LABEL_KEYS: Record<UserColumn, string> = {
  [userColumns.NAME]: 'user.table.columns.name',
  [userColumns.STATUS]: 'user.table.columns.status',
  [userColumns.ROLE]: 'user.table.columns.role',
  [userColumns.ACCESS]: 'user.table.columns.access',
  [userColumns.FILES]: 'user.table.columns.client_files',
  [userColumns.LAST_LOGIN]: 'user.table.columns.last_login',
  [userColumns.ACTIONS]: 'user.table.columns.action',
}

// The design fixes these two columns, so the name has room for an avatar over two
// lines and the pencil sits clear of the right edge. The rest share what is left.
export const COLUMN_CLASSES: Partial<Record<UserColumn, string>> = {
  [userColumns.NAME]: 'tw-w-[240px]',
  [userColumns.ACTIONS]: 'tw-w-[81px]',
}

export const STATUS_VARIANTS: Record<UserStatus, StaticPillVariant> = {
  active: staticPillVariants.SUCCESS,
  unconfirmed: staticPillVariants.WARNING,
  deactivated: staticPillVariants.NEUTRAL,
}

export const STATUS_KEYS: Record<UserStatus, string> = {
  active: 'user.table.status.active',
  deactivated: 'user.table.status.deactivated',
  unconfirmed: 'user.table.status.unconfirmed',
}

export const ROLE_KEYS: Record<UserRole, string> = {
  admin: 'user.table.role.admin',
  contributor: 'user.table.role.contributor',
  pulse: 'user.table.role.pulse',
  staff: 'user.table.role.staff',
}

export const ROLE_OPTION_KEYS = [
  {
    value: userRoles.ADMIN,
    label: 'user.table.role.admin',
    description: 'user.panel.role_admin_description',
  },
  {
    value: userRoles.STAFF,
    label: 'user.table.role.staff',
    description: 'user.panel.role_staff_description',
  },
] as const

export const EXTERNAL_ROLE_OPTION_KEYS = [
  {
    value: userRoles.CONTRIBUTOR,
    label: 'user.table.role.contributor',
    description: 'user.panel.role_contributor_description',
  },
  {
    value: userRoles.PULSE,
    label: 'user.table.role.pulse',
    description: 'user.panel.role_pulse_description',
  },
] as const

export const TWO_FACTOR_STATUS_KEYS: Record<TwoFactorStatus, string> = {
  [twoFactorStatuses.DISABLED]: 'user.panel.two_factor_status.disabled',
  [twoFactorStatuses.ENABLED]: 'user.panel.two_factor_status.enabled',
  [twoFactorStatuses.PENDING]: 'user.panel.two_factor_status.pending',
  [twoFactorStatuses.RESET]: 'user.panel.two_factor_status.reset',
  [twoFactorStatuses.SKIPPED]: 'user.panel.two_factor_status.skipped',
}

export const ACCESS_KEYS: Record<UserAccess, string> = {
  all_files: 'user.table.access.multifile',
  limited: 'user.table.access.limited',
}

export const FIRST_PAGE = 1

// The picker lists results before anything is typed, and an empty query
// matches every client file the admin can reach.
export const SEARCH_LIMIT = 5

export const USERS_REQUEST_KEY = 'usersList' as const

export const STALE_USERS_REQUEST_REASON =
  'UserManagement::UsersList::StaleRequest'

export const UNMOUNTED_USERS_REQUEST_REASON =
  'UserManagement::UsersList::Unmounted'

export const USER_PANEL_REQUEST_KEY = 'userPanel' as const

export const STALE_USER_PANEL_REQUEST_REASON =
  'UserManagement::UserPanel::StaleRequest'

// What the panel reads before a user has loaded. The footer mail action has no
// "none" value, and nothing shows it until the load answers.
export const NO_CAPABILITIES: UserPanelCapabilities = {
  emailEditable: false,
  vismaConnectFirm: false,
  localeOptions: [],
  staffAccessEditable: false,
  lastAdminInFirm: false,
  companyCreationAvailable: false,
  groupsSupported: false,
  ssoSupported: false,
  ssoLockedByVismaConnect: false,
  firmFilesCount: 0,
  canResetTwoFactor: false,
  twoFactorVisible: false,
  footerMailAction: footerMailActions.PASSWORD_RESET,
  deactivateBlockedReason: null,
}

export const UNPROCESSABLE_ENTITY = 422

// `users#create` reads the flag with `present?`, so the empty string is the
// "do not send it" value. The Slim create form posts the same pair.
export const WELCOME_MAIL = 'welcome' as const

export const NO_WELCOME_MAIL = '' as const

export const DESTROY = '1' as const

export const GROUPS_REQUEST_KEY = 'groupsList' as const

export const STALE_GROUPS_REQUEST_REASON =
  'UserManagement::GroupsList::StaleRequest'

export const QUERY_KEYS = {
  SORT_BY: 'sort_by',
  SORT_DIRECTION: 'sort_direction',
  PAGE: 'page',
  SEARCH: 'q',
  ROLE: 'role',
  STATUS: 'status',
  LAST_LOGIN: 'last_login',
  USER: 'user',
} as const

// "All" is the absence of a filter, not another member of the role, the status
// or the last-login set: it carries the empty value that leaves the URL.
export const ALL_FILTER_VALUE = ''

const ROLE_FILTER_KEYS: Record<UserRole, string> = {
  [userRoles.ADMIN]: 'user.filters.role.admin',
  [userRoles.STAFF]: 'user.filters.role.staff',
  [userRoles.CONTRIBUTOR]: 'user.filters.role.contributor',
  [userRoles.PULSE]: 'user.filters.role.pulse',
}

const roleFilterOptions = (list: UserList): FilterOption[] => [
  { value: ALL_FILTER_VALUE, labelKey: 'user.filters.role.all' },
  ...userListRoles[list].map((role) => ({
    value: role,
    labelKey: ROLE_FILTER_KEYS[role],
  })),
]

export const ROLE_FILTER_OPTIONS: Record<UserList, FilterOption[]> = {
  [userLists.STAFF]: roleFilterOptions(userLists.STAFF),
  [userLists.EXTERNAL]: roleFilterOptions(userLists.EXTERNAL),
}

export const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { value: ALL_FILTER_VALUE, labelKey: 'user.filters.status.all' },
  { value: userStatuses.ACTIVE, labelKey: 'user.filters.status.active' },
  {
    value: userStatuses.UNCONFIRMED,
    labelKey: 'user.filters.status.unconfirmed',
  },
  {
    value: userStatuses.DEACTIVATED,
    labelKey: 'user.filters.status.deactivated',
  },
]

export const LAST_LOGIN_FILTER_OPTIONS: FilterOption[] = [
  { value: ALL_FILTER_VALUE, labelKey: 'user.filters.last_login.all' },
  {
    value: userLastLoginFilters.OVER_1_MONTH,
    labelKey: 'user.filters.last_login.over_1_month',
  },
  {
    value: userLastLoginFilters.OVER_1_YEAR,
    labelKey: 'user.filters.last_login.over_1_year',
  },
  {
    value: userLastLoginFilters.OVER_2_YEARS,
    labelKey: 'user.filters.last_login.over_2_years',
  },
  {
    value: userLastLoginFilters.NEVER,
    labelKey: 'user.filters.last_login.never',
  },
]

export const GROUP_COLUMNS: GroupColumn[] = [
  groupColumns.NAME,
  groupColumns.FILES,
  groupColumns.MEMBERS,
  groupColumns.ACTIONS,
]

export const GROUP_COLUMN_LABEL_KEYS: Record<GroupColumn, string> = {
  [groupColumns.NAME]: 'group.table.columns.name',
  [groupColumns.FILES]: 'group.table.columns.files',
  [groupColumns.MEMBERS]: 'group.table.columns.staff',
  [groupColumns.ACTIONS]: 'group.table.columns.action',
}

export const GROUP_COLUMN_CLASSES: Partial<Record<GroupColumn, string>> = {
  [groupColumns.NAME]: 'tw-w-[389px]',
}
